/**
 * TG-5 (m1-exposure-detail-sourcing, AC-1): declares, for every H2 exposure
 * question that carries a data-on-file help icon, which mapping-artefact
 * screen backs it and which attribute(s) build the picker's option label
 * and a repeat item's prefill fields -- the one place a `screenKey` or
 * field-map attribute NAME is written down for this feature's web tier.
 * `on-file-fields.test.ts` loads `src/shared/field_map/v2.json` read-only
 * and asserts every one of those names actually exists on the named
 * screen, so a renamed or removed field-map attribute fails a fast,
 * obvious test here rather than a silent runtime gap in production.
 *
 * This module declares NAMES only, never attribute VALUES and never the
 * full list of attributes a help panel line prints: `OnFilePanel.tsx`
 * renders every attribute `OnFileScreenItem.attributes` actually carries
 * for a given item, humanised, with no fixed whitelist here -- adding a
 * field-map attribute to a screen (a mapping-artefact change, not a web
 * change) makes it appear in the panel with NO change to this file or
 * `on-file-fields.test.ts`'s own fixture test proves exactly that for the
 * vehicle screen.
 *
 * `labelAttributes` is the one place this module DOES fix a short,
 * specific attribute list: the ordered fields used to build one item's
 * one-line heading (a vehicle's year, make and model, a hazard's class
 * code and classification) -- used identically by the help panel's
 * heading and, where a picker exists, by the picker's own option label,
 * so the two are never free to say something different about the same
 * on-file item.
 *
 * Every question H2 gives a help icon has an entry (owner decision 2026-11-03,
 * m1-portal-h2-fidelity, closing DEBT-20261016-exq1): the icon always shows.
 * Where no field-map screen holds the records (liquor liability, commercial
 * watercraft, workers' compensation class codes), `screenKey` is omitted, the
 * help panel shows the default "We do not have any ... on file" message and
 * the picker has nothing to offer. Mapping gaps are closed during UAT.
 *
 * OBJ-R-21/FIND-978 (TG-P2-DEBATE-LOOP): marina, marine employers,
 * transportation and commercial trucking each map (see the mapping
 * artefact for the specific line type codes) to a line type whose
 * field-map screen IS real -- general_liability_hazard for the first two
 * (the same screen general liability's own class-code question already
 * declares), transportation_operations_free_on_board /
 * transportation_vehicle / transportation_driver for transportation, and
 * truckers_principal_shipper / truckers_commodity / truckers_vehicle /
 * truckers_driver for commercial trucking. All four are declared below,
 * following the general-liability class-code picker and the
 * commercial-auto vehicle/driver pattern exactly.
 */
import type { SectionId } from "../../routing/route-table";
import { exposureContent } from "./exposure-content";

/** One prefill target: a repeat item's OWN field (`RepeatFieldSpec.id`,
 * the SAME item the picker field lives on) populated from the picked
 * on-file item's named attribute once a real pick is made. */
export interface OnFilePrefillSpec {
  /** The repeat item's own field id this prefill writes into. */
  readonly fieldId: string;
  /** The on-file attribute name read to populate it. Not required to
   * exist on every stored item -- `OnFilePanel`/`OnFilePicker` render a
   * gap (AC-3: "an item missing an attribute ... the prefill field is
   * empty and editable"), never a guessed or defaulted value, when a
   * specific item's own record does not carry it. */
  readonly attribute: string;
}

/** The picker half of a data-on-file question: which repeat field is the
 * "Which X?" select, which on-file screen it picks from, and which other
 * fields on the SAME repeat item it prefills once a real item is picked. */
export interface OnFilePickerSpec {
  /** The `RepeatFieldSpec.id` of the picker field itself within the
   * question's `repeat.fields` (or `"property"` for a limits question). */
  readonly fieldId: string;
  /** Omitted where no field-map screen holds these records: nothing to pick. */
  readonly screenKey?: string;
  readonly labelAttributes: readonly string[];
  readonly prefill: readonly OnFilePrefillSpec[];
}

/** The help-panel half: which screen backs the question's help icon, and
 * which attributes build each item's one-line heading.
 *
 * `siblingScreenKeys` (OBJ-R-20/FIND-973): a section's mapped Epic line
 * types do not all store the one concept a help panel asks about under
 * the same field-map screen key (commercial property's premises, the
 * equipment floater's equipment). `screenKey` names the primary screen;
 * `findOnFileScreen` merges every matched screen's items into one panel.
 * `screenKey` is omitted where no screen holds the records at all. */
export interface OnFileHelpLineSpec {
  readonly screenKey?: string;
  readonly siblingScreenKeys?: readonly string[];
  readonly labelAttributes: readonly string[];
}

/** H2 `soldAware`/`soldSource`: the question whose picks flag this question's on-file records
 * (Sold, Replaced, Removed), and whose `soldOutcome` picks drop out of a `soldAware` picker. */
export interface OnFileSoldSourceSpec {
  readonly fieldId: string;
  readonly pickerFieldId: string;
  readonly soldOutcome: string;
}

/** H2 per-property limits: the subjects of insurance a picked building holds on file. */
export interface OnFileLimitsSubjectsSpec {
  readonly screenKey: string;
  /** The attribute a subject shares with the picked building. */
  readonly matchAttribute: string;
  readonly labelAttribute: string;
  readonly amountAttribute: string;
}

export interface SectionOnFileSpec {
  readonly sectionId: SectionId;
  /** The `ExposureQuestion.fieldId` this help icon/picker belongs to. */
  readonly fieldId: string;
  /** The plural noun the default wordings name the stored items by
   * (`exposure-content.ts`'s `onFileNothingOnFile`). */
  readonly itemsLabel: string;
  /** H2's `hintLabel`: the help trigger's accessible name. */
  readonly helpTriggerLabel?: string;
  readonly helpLine: OnFileHelpLineSpec;
  readonly picker?: OnFilePickerSpec;
  readonly soldSource?: OnFileSoldSourceSpec;
  readonly limitsSubjects?: OnFileLimitsSubjectsSpec;
}

const VEHICLE_LABEL = ["year", "make", "model", "vin"] as const;
const BUILDING_LABEL = ["locationNumber", "buildingNumber"] as const;
const CLASS_CODE_LABEL = ["classCode", "classification"] as const;
const BUILDINGS_HELP: OnFileHelpLineSpec = {
  screenKey: "property_premise",
  siblingScreenKeys: ["cbop_premise_coverage"],
  labelAttributes: BUILDING_LABEL,
};
const BUILDINGS_SOLD: OnFileSoldSourceSpec = { fieldId: "buildingsChanged", pickerFieldId: "whichBuilding", soldOutcome: "Sold" };

function picker(fieldId: string, screenKey: string | undefined, labelAttributes: readonly string[], prefill: readonly OnFilePrefillSpec[] = []): OnFilePickerSpec {
  return { fieldId, ...(screenKey !== undefined ? { screenKey } : {}), labelAttributes, prefill };
}

/** H2's item-change question (vehicles, watercraft, equipment): flagged and excluded by its own picks. */
function itemChange(
  sectionId: SectionId,
  fieldId: string,
  pickerFieldId: string,
  itemsLabel: string,
  helpTriggerLabel: string,
  helpLine: OnFileHelpLineSpec,
): SectionOnFileSpec {
  return {
    sectionId,
    fieldId,
    itemsLabel,
    helpTriggerLabel,
    helpLine,
    picker: picker(pickerFieldId, helpLine.screenKey, helpLine.labelAttributes),
    soldSource: { fieldId, pickerFieldId, soldOutcome: "Sold" },
  };
}

/** H2's driver or operator question: a removed person is flagged in the help list. */
function personChange(sectionId: SectionId, fieldId: string, pickerFieldId: string, itemsLabel: string, screenKey: string | undefined): SectionOnFileSpec {
  const helpLine: OnFileHelpLineSpec = { ...(screenKey !== undefined ? { screenKey } : {}), labelAttributes: ["name"] };
  return {
    sectionId,
    fieldId,
    itemsLabel,
    helpTriggerLabel: `Which ${itemsLabel} do we hold on file?`,
    helpLine,
    picker: picker(pickerFieldId, screenKey, ["name"]),
    soldSource: { fieldId, pickerFieldId, soldOutcome: "Removed" },
  };
}

function classCodes(sectionId: SectionId): SectionOnFileSpec {
  return {
    sectionId,
    fieldId: "grossSalesChanged",
    itemsLabel: "class codes",
    helpTriggerLabel: exposureContent.onFileGeneralLiabilityHelpTriggerLabel,
    helpLine: { screenKey: "general_liability_hazard", labelAttributes: CLASS_CODE_LABEL },
    picker: picker("classCode", "general_liability_hazard", CLASS_CODE_LABEL, [{ fieldId: "premiumBasis", attribute: "premiumBasis" }]),
  };
}

export const ON_FILE_FIELDS: readonly SectionOnFileSpec[] = [
  {
    sectionId: "exposure-commercial-property",
    fieldId: "buildingsChanged",
    itemsLabel: "buildings",
    helpTriggerLabel: "Which buildings do we hold on file?",
    helpLine: BUILDINGS_HELP,
    picker: picker("whichBuilding", "property_premise", BUILDING_LABEL),
    soldSource: BUILDINGS_SOLD,
  },
  {
    sectionId: "exposure-commercial-property",
    fieldId: "buildingSystemsImproved",
    itemsLabel: "buildings",
    helpTriggerLabel: "Which buildings do we hold on file?",
    helpLine: BUILDINGS_HELP,
    picker: picker("property", "property_premise", BUILDING_LABEL),
    soldSource: BUILDINGS_SOLD,
  },
  {
    sectionId: "exposure-commercial-property",
    fieldId: "limitsChanged",
    itemsLabel: "limits",
    helpLine: BUILDINGS_HELP,
    picker: picker("property", "property_premise", BUILDING_LABEL),
    soldSource: BUILDINGS_SOLD,
    limitsSubjects: {
      screenKey: "property_subject_of_insurance",
      matchAttribute: "locationNumber",
      labelAttribute: "subjectOfInsurance",
      amountAttribute: "amount",
    },
  },
  itemChange("exposure-commercial-auto", "vehiclesChanged", "whichVehicle", "vehicles", "Which vehicles do we hold on file?", {
    screenKey: "business_auto_vehicle",
    labelAttributes: VEHICLE_LABEL,
  }),
  personChange("exposure-commercial-auto", "driversChanged", "whichDriver", "drivers", "business_auto_driver"),
  itemChange(
    "exposure-commercial-watercraft",
    "watercraftChanged",
    "which",
    "watercraft, engines or trailers",
    "Which watercraft, engines, and trailers do we hold on file?",
    { labelAttributes: [] },
  ),
  personChange("exposure-commercial-watercraft", "operatorsChanged", "whichOperator", "operators", undefined),
  classCodes("exposure-general-liability"),
  {
    sectionId: "exposure-liquor-liability",
    fieldId: "grossSalesChanged",
    itemsLabel: "estimated gross sales",
    helpTriggerLabel: "What are the estimated gross sales on file?",
    helpLine: { labelAttributes: [] },
    picker: picker("description", undefined, []),
  },
  classCodes("exposure-marina-operators-legal-liability"),
  classCodes("exposure-marine-employers-legal-liability"),
  {
    sectionId: "exposure-transportation",
    fieldId: "grossSalesChanged",
    itemsLabel: "gross sales figures",
    helpTriggerLabel: "What is the estimated gross sales on file?",
    helpLine: { screenKey: "transportation_operations_free_on_board", labelAttributes: ["annualGrossSales"] },
  },
  {
    sectionId: "exposure-transportation",
    fieldId: "unitCountsChanged",
    itemsLabel: "unit counts",
    helpTriggerLabel: "What unit counts do we hold on file?",
    helpLine: {
      screenKey: "transportation_operations_free_on_board",
      labelAttributes: ["trucksOperated", "tractorsOperated", "trailersOperated", "tankTrucksOperated", "refrigeratedUnitsOperated"],
    },
  },
  itemChange("exposure-transportation", "vehiclesChanged", "whichVehicle", "vehicles", "Which vehicles do we hold on file?", {
    screenKey: "transportation_vehicle",
    labelAttributes: VEHICLE_LABEL,
  }),
  personChange("exposure-transportation", "driversChanged", "whichDriver", "drivers", "transportation_driver"),
  {
    sectionId: "exposure-commercial-trucking",
    fieldId: "grossSalesChanged",
    itemsLabel: "gross receipts figures",
    helpTriggerLabel: "What is the estimated gross sales on file?",
    helpLine: { screenKey: "truckers_principal_shipper", labelAttributes: ["grossReceiptsPastYear"] },
  },
  {
    sectionId: "exposure-commercial-trucking",
    fieldId: "commoditiesChanged",
    itemsLabel: "commodities",
    helpTriggerLabel: "Which commodities do we hold on file?",
    helpLine: { screenKey: "truckers_commodity", labelAttributes: ["commoditiesTransported"] },
    picker: picker("commodity", "truckers_commodity", ["commoditiesTransported"], [
      { fieldId: "percentageOfRevenue", attribute: "percentTotalRevenue" },
      { fieldId: "valuePerTruckLoad", attribute: "valuePerTruckLoad" },
    ]),
  },
  itemChange("exposure-commercial-trucking", "fleetChanged", "whichVehicle", "vehicles", "Which vehicles do we hold on file?", {
    screenKey: "truckers_vehicle",
    labelAttributes: VEHICLE_LABEL,
  }),
  personChange("exposure-commercial-trucking", "driversChanged", "whichDriver", "drivers", "truckers_driver"),
  {
    sectionId: "exposure-workers-compensation",
    fieldId: "payrollChanged",
    itemsLabel: "payroll or employee details",
    helpTriggerLabel: "What payroll and employee details do we hold on file?",
    helpLine: { screenKey: "workers_compensation_location", labelAttributes: ["street1", "city", "state"] },
    // Epic holds class codes inside each location's rating information, which the field map does
    // not flatten into records to pick from.
    picker: picker("classCodeAndLocation", undefined, []),
  },
  {
    sectionId: "exposure-workers-compensation",
    fieldId: "employeeCountChanged",
    itemsLabel: "owners, officers or partners",
    helpTriggerLabel: "Which owners, officers, and partners do we hold on file?",
    helpLine: { screenKey: "workers_compensation_individual", labelAttributes: ["name"] },
    picker: picker("whichPerson", "workers_compensation_individual", ["name"]),
    soldSource: { fieldId: "employeeCountChanged", pickerFieldId: "whichPerson", soldOutcome: "Removed" },
  },
  itemChange(
    "exposure-inland-marine-equipment-floater",
    "equipmentChanged",
    "whichEquipment",
    "equipment",
    "Which equipment do we hold on file?",
    {
      screenKey: "equipment_floater_scheduled_equipment",
      siblingScreenKeys: ["edp_schedule_of_equipment"],
      // OBJ-C-13/FIND-975: attributes both the primary and the sibling screen carry.
      labelAttributes: ["manufacturer", "model", "serialNumber"],
    },
  ),
] as const;

/** Looks up one question's data-on-file declaration, or `undefined` for a question with no
 * on-file help icon. */
export function getOnFileFieldSpec(sectionId: SectionId, fieldId: string): SectionOnFileSpec | undefined {
  return ON_FILE_FIELDS.find((spec) => spec.sectionId === sectionId && spec.fieldId === fieldId);
}
